/**
 * Memory (§4.11; wave C, decision 0071, spec §4). People write at once (WriteMemory: Teach, Remember this, Edit,
 * Forget, Restore). Agents and a person's own CLI use one tool, `remember` (ProposeMemory), routed here (§4.3):
 *
 * | Who                                              | Rule              | Preference, fact, learning | Edit or forget        |
 * | a person's Claude Code / Codex (person MCP)      | applies           | applies, "via Claude Code" | applies              |
 * | a lead or area lead, turn a person started       | applies           | applies at once            | applies              |
 * | same, turn changes started, at Act               | applies           | applies at once            | applies              |
 * | same, at Ask first                               | card              | card                       | card                  |
 * | same, at Suggest                                 | refused with the level's sentence                               |
 * | a worker                                         | the lead decides at Act (`leadMayDecide`), else a card                    |
 *
 * Decision 0134 (0133 §3): a tainted run routes like any other; its memory keeps `tainted` (quoted when loaded).
 * A card's recipient is the turn's asker when a person asked, else the keeper. Project, area and item scopes live in
 * this log; user scope is written by people only (no agent writes personal memory, wave C). Every new memory gets its
 * ref `M-n` here; a revision and a restore keep it. Edit and Restore keep provenance (`tainted`, `sourceRunId`,
 * `source`); taint clears only when a person changes the body, and a person's Edit marks the item checked.
 */

import { autonomyOf, levelOf } from "../autonomy"
import { chainApprover } from "../card-chain"
import { memoryRef, numberMemory } from "../knowledge/memory-refs"
import { personClaimOf } from "../knowledge/memory-validity"
import { routeCard, workspaceOwnerOf } from "../multiplayer"
import { capabilityVerdict, policyAgentOf } from "../policy"
import { openProposal } from "../runs"
import { agentOf, itemOf, leadOf, memberOf, memoryOf, must, runOf } from "../state"
import type {
  Actor,
  Command,
  Ids,
  MemoryDraft,
  MemoryItem,
  MemoryScope,
  ProjectState,
  Proposal,
  ProposalBody,
} from "../types"
import { actorTaint, forbidden, invalid, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>
type MemorySource = NonNullable<MemoryDraft["source"]>

/** Scopes stored in the Project DO. */
export const isProjectScope = (scope: MemoryScope): boolean =>
  scope._tag === "project" || scope._tag === "area" || scope._tag === "item"

/**
 * Decision 0139 (memory phase 1, 1.0b): where memory that is not this project's goes instead. The project log is the
 * only memory store and nothing loads workspace, personal or agent-scope items, so such a write is refused up front
 * with the home that already exists for it, never accepted and then dropped (approved cards used to be).
 */
export const memoryElsewhere = (scope: MemoryScope | null | undefined, op: string): string | null => {
  if (op === "promote" || scope?._tag === "workspace")
    return "memory is per project: knowledge for the whole workspace goes in a workspace page (propose_page workspace/guidelines/<topic>.md)"
  if (scope?._tag === "user")
    return "memory is per project: a person's own notes are private notes (remember with scope personal: only they and their own agents see them); a preference about someone is project memory with `about`"
  if (scope?._tag === "agent")
    return "memory is per project: an agent's own notes go in its notebook (agents/<handle>/notebook.md)"
  return null
}

/** The ref the next new memory gets (M-n), counting items from before the wave (`numberMemory`). */
export const nextMemoryRef = (s: ProjectState): Ids["ShortRef"] =>
  memoryRef((numberMemory(s).refCounters.M ?? 0) + 1)

/** Build the stored item from a draft (user scope is always private). */
export const memoryItemOf = (
  base: {
    readonly ownerId: Ids["UserId"]
    readonly actor: Actor
    readonly now: number
    readonly seq: number
  },
  itemId: Ids["MemoryItemId"],
  draft: MemoryDraft,
  extra: {
    revision: number
    tainted: boolean
    reviewedBy: Actor | null
    proposalId: Ids["ProposalId"] | null
    sourceRunId: Ids["RunId"] | null
    createdBy?: Actor
    createdAt?: number
    /** Wave C: M-n (new: the next ref; a revision: the item's own). */
    ref?: Ids["ShortRef"] | undefined
    /** Wave C: a person's CLI over the person MCP. */
    via?: "person_mcp" | undefined
    /** Wave C: where it came from, when the draft names none (a revision keeps the item's). */
    source?: MemorySource | undefined
  },
): MemoryItem => {
  const source = draft.source ?? extra.source
  return {
    ownerId: draft.scope._tag === "user" ? draft.scope.userId : base.ownerId,
    visibility: draft.scope._tag === "user" ? "private" : "workspace",
    createdBy: extra.createdBy ?? base.actor,
    createdAt: extra.createdAt ?? base.now,
    updatedBy: base.actor,
    updatedAt: base.now,
    lastSeq: (base.seq + 1) as Ids["Seq"],
    contentSeq: (base.seq + 1) as Ids["Seq"],
    ...draft,
    ...(source !== undefined ? { source } : {}),
    id: itemId,
    revision: extra.revision,
    status: "accepted",
    tainted: extra.tainted,
    reviewedBy: extra.reviewedBy,
    proposalId: extra.proposalId,
    sourceRunId: extra.sourceRunId,
    ...(extra.ref !== undefined ? { ref: extra.ref } : {}),
    ...(extra.via !== undefined ? { via: extra.via } : {}),
  }
}

/**
 * Whom a memory or monitor card is for (spec §4.3, E's one asker per turn): the run's asker when a person asked (still a
 * working member), else the project's admins, else the workspace owner (decision 0127: no longer the agent's keeper).
 * A person's own write: that person.
 */
export const cardRecipientOf = (s: ProjectState, actor: Actor): Ids["UserId"] =>
  routeCard(s, actor) ?? workspaceOwnerOf(s) ?? must(s.project, "project").ownerId

const leadScope = (scope: MemoryScope): boolean => scope._tag === "area" || scope._tag === "project"

/**
 * Working knowledge the lead may decide: area or project scope, not locked, and not its own proposal. Judged on the
 * draft AND existing item: locked items go to a person. Decision 0134 (0133 §3): taint is recorded, not a gate. A create or revise needs evidence;
 * an archive (curation: past its
 * revisit date, a merged duplicate) is judged on the existing item alone. Whether the lead may actually decide
 * also depends on its curate_memory capability (mayDecide).
 */
const leadMayDecide = (
  actor: Actor,
  op: Cmd<"ProposeMemory">["op"],
  draft: MemoryDraft | null,
  existing: MemoryItem | undefined,
  evidence: ReadonlyArray<unknown>,
  leadId: Ids["AgentId"] | undefined,
): boolean => {
  if (actor._tag !== "agent" && actor._tag !== "worker") return false // a person's own proposal: they confirm it
  if (leadId === undefined || (actor._tag === "agent" && actor.agentId === leadId)) return false
  if (op === "promote") return false
  if (existing?.locked) return false
  if (existing && !leadScope(existing.scope)) return false
  if (op === "archive") return existing !== undefined && existing.status === "accepted"
  return draft !== null && leadScope(draft.scope) && !draft.locked && evidence.length > 0
}

/** The Suggest level's sentence for `remember` (F17: it lives here, not in autonomy.ts). */
export const REMEMBER_AT_SUGGEST =
  "Suggest in chat: changes started this turn. Say what you would remember; the person can press Remember this."

/**
 * Does `source` exist in this project? A chat message: the agent's chat (its id is checked by the tool layer); a
 * comment: open asks, else the comments projection (`ctx.history.comment`; without it only a person's write passes);
 * a record: an item, goal, KPI, card, output or memory by its ref; a run; `typed` is a person's own words.
 */
const sourceProblem = (tx: Tx, source: MemorySource | undefined, strict: boolean): string | null => {
  if (source === undefined) return null
  const s = tx.state
  switch (source._tag) {
    case "message":
      return agentOf(s, source.agentId) ? null : "that chat is not in this project"
    case "comment": {
      if (s.openAsks.some((c) => c.id === source.commentId)) return null
      const lookup = tx.ctx.history?.comment
      if (!lookup) return strict ? "that comment can't be checked here" : null
      return lookup(source.commentId) ? null : "that comment is not in this project"
    }
    case "record": {
      const ref = source.ref
      const found =
        s.items.some((x) => x.ref === ref) ||
        s.goals.some((x) => x.ref === ref) ||
        s.kpis.some((x) => x.ref === ref) ||
        s.proposals.some((x) => x.ref === ref) ||
        s.artifacts.some((x) => x.ref === ref) ||
        s.runs.some((x) => x.ref === ref) ||
        s.memory.some((x) => x.ref === ref)
      return found ? null : `${ref} is not in this project`
    }
    case "run": {
      const own = (tx.actor._tag === "agent" || tx.actor._tag === "worker") && tx.actor.runId === source.runId
      return own || runOf(s, source.runId) ? null : "that run is not in this project"
    }
    case "typed":
      return tx.actor._tag === "user" ? null : "only a person's own words are typed"
  }
}

/** A preference is about a member of the project (C1); checked for every writer. */
const aboutProblem = (s: ProjectState, draft: MemoryDraft | null): string | null => {
  if (draft?.content._tag !== "preference") return null
  const about = draft.content.about
  if (about === undefined) return null
  return s.members.length === 0 || memberOf(s, about) ? null : "a preference is about someone in this project"
}

/** Decision 0084 (§6.4): the Invalid text for a new memory rule. */
export const RULES_LIVE_IN_GUIDELINES = "rules live in guidelines/: write or propose the guideline"

/**
 * Decision 0084 (§6.4): memory takes no new rules, from anyone. Revising, archiving and forgetting an existing rule
 * still works; turning another kind into a rule is a new rule.
 */
const refuseNewRule = (
  op: string,
  draft: { readonly content: { readonly _tag: string } } | null | undefined,
  existing: MemoryItem | undefined,
): void => {
  if (draft?.content._tag !== "rule") return
  if (op === "create" || (op === "revise" && existing !== undefined && existing.content._tag !== "rule"))
    invalid(RULES_LIVE_IN_GUIDELINES, "draft.content")
}

type Route =
  | { readonly _tag: "apply" }
  | { readonly _tag: "card"; readonly to: Ids["UserId"]; readonly byLead: boolean }

export const proposeMemory = (tx: Tx, c: Cmd<"ProposeMemory">): void => {
  const s = tx.state
  // decide re-checks the capability the tool list was built from (§8.6)
  const agent = policyAgentOf(s, tx.actor)
  if (agent) {
    const areaId =
      tx.actor._tag === "worker" ? (itemOf(s, tx.actor.itemId)?.areaId ?? agent.areaId) : agent.areaId
    if (capabilityVerdict(s, agent, "propose_memory", areaId) === "never")
      forbidden("policy_never", "the agent's policy says never to propose_memory")
  }
  if (s.proposals.some((p) => p.id === c.proposalId)) invalid("proposal id already exists", "proposalId")
  // decision 0146: a re-check against the record stamps the item, nothing else (never a card, no new revision)
  if (c.op === "confirm") {
    confirmMemory(tx, c)
    return
  }
  if (c.op !== "archive" && c.draft === null) invalid(`${c.op} needs a draft`, "draft")
  if (c.draft?.locked) invalid("locked items are written by people only", "draft.locked")
  if (c.op === "create" && memoryOf(s, c.itemId)) invalid("memory item id already exists", "itemId")
  if (c.op === "promote" && c.fromScope === null) invalid("promote needs fromScope", "fromScope")
  const named = c.draft?.scope ?? c.fromScope
  if (named && "projectId" in named && s.project && named.projectId !== s.project.id)
    invalid("the scope names another project", "draft.scope")
  const existing = memoryOf(s, c.itemId)
  refuseNewRule(c.op, c.draft, existing)
  const superseded = supersededOf(tx, c.op, c.supersedes)
  if (
    (c.op === "revise" || c.op === "archive") &&
    existing === undefined &&
    (named === null || isProjectScope(named))
  )
    invalid("memory item does not exist here", "itemId")
  const intentKey = c.supersedes ? `memory:supersede:${c.supersedes}` : `memory:${c.op}:${c.itemId}`
  const dup = s.proposals.find(
    (p) => p.intentKey === intentKey && (p.state === "open" || p.state === "revision_requested"),
  )
  if (dup) invalid(`${dup.ref} already proposes this memory change`, "itemId")
  const tainted = actorTaint(s, tx.actor) === "tainted"
  const lead = leadOf(s)
  const scope = c.draft?.scope ?? c.fromScope ?? existing?.scope ?? null
  const userScope = scope?._tag === "user" ? scope.userId : null
  const a = tx.actor
  // wave E (decision 0063): personal memory is proposed only into a person's own scope. Wave C (decision 0071, C1): no
  // agent writes personal memory at all; a preference is project memory about a person (`about`)
  if (userScope !== null) {
    if (a._tag !== "user" || a.userId !== userScope)
      forbidden(
        "not_owner",
        "not_yours: agents don't write personal memory; a preference is project memory about a person (about)",
      )
  }
  const elsewhere = memoryElsewhere(scope, c.op)
  if (elsewhere) invalid(elsewhere, c.op === "promote" ? "op" : "draft.scope")
  const problem = sourceProblem(tx, c.draft?.source, a._tag !== "user") ?? aboutProblem(s, c.draft)
  if (problem) invalid(problem, c.draft?.source ? "draft.source" : "draft.content.about")
  const route = routeOf(tx, c, existing, userScope !== null, superseded)
  if (route._tag === "apply") {
    applyNow(tx, c, existing, superseded)
    return
  }
  const sourceRunId = a._tag === "agent" || a._tag === "worker" ? a.runId : null
  const body: Extract<ProposalBody, { _tag: "memory" }> = {
    _tag: "memory",
    op: c.op,
    itemId: c.itemId,
    draft: c.draft,
    fromScope: c.fromScope,
    evidence: c.evidence,
    sourceRunId,
    ...(superseded ? { supersedes: superseded.id } : {}),
  }
  openProposal(tx, {
    id: c.proposalId,
    ownerId: route.to,
    body,
    proposedBy: a,
    reasoning: c.reasoning,
    oneLine: c.draft
      ? superseded
        ? `supersede ${superseded.ref ?? superseded.title} with ${c.draft.content._tag}: ${c.draft.title}`
        : `${c.op} ${c.draft.content._tag}: ${c.draft.title}`
      : `${c.op} memory ${c.itemId}`,
    risk: c.draft?.content._tag === "rule" ? "medium" : "low",
    reversibility: "reversible",
    tier: route.byLead ? "fyi" : "soon",
    blocks: [],
    tainted,
    // decision 0106: an agent's memory card for a person passes the 0102 chain for the lead's recommendation first;
    // it stays a person's decision (`isPersonOnlyCard`)
    approver:
      route.byLead && lead
        ? { _tag: "lead", agentId: lead.id, escalateTo: route.to }
        : chainApprover(s, a, { _tag: "human", userId: route.to }, { body, blocks: [] }),
    decideBy: null,
    expiresAt: null, // rev 2.1 (decision 0018): proposals do not expire
    intentKey,
  })
}

/** The §4.3 routing of one `remember`. Throws Forbidden at Suggest. */
const routeOf = (
  tx: Tx,
  c: Cmd<"ProposeMemory">,
  existing: MemoryItem | undefined,
  personal: boolean,
  superseded?: MemoryItem,
): Route => {
  const s = tx.state
  const a = tx.actor
  const to = cardRecipientOf(s, a)
  const card = (byLead = false): Route => ({ _tag: "card", to, byLead })
  // decision 0146 (0140 §17): an agent supersedes a person's claim only by proposal, to that person while they are a
  // member (the old claim is kept either way), unless the turn is that person's own ask. Non-person memory stays the
  // lead's call (0133). A revision (`replaces`, a correction of the same claim) keeps its 0139 routing: its history is
  // the revision events, and an agent's revision leaves the claim unchecked
  const claimant = superseded && a._tag !== "user" ? personClaimOf(superseded) : null
  const asker = a._tag === "agent" ? runOf(s, a.runId)?.askedBy : undefined
  const askedByClaimant = asker?._tag === "user" && asker.userId === claimant
  if (claimant !== null && !askedByClaimant && (a._tag === "agent" || a._tag === "worker")) {
    if (a._tag === "agent" && autonomyOf(s, a) === "suggest") forbidden("autonomy", REMEMBER_AT_SUGGEST)
    const member = memberOf(s, claimant as Ids["UserId"])
    return { _tag: "card", to: member ? (claimant as Ids["UserId"]) : to, byLead: false }
  }
  // what never applies at once, whoever writes it
  const guarded =
    c.op === "promote" ||
    personal ||
    existing?.locked === true ||
    superseded?.locked === true ||
    (c.draft !== null && !isProjectScope(c.draft.scope)) ||
    (existing !== undefined && !isProjectScope(existing.scope))
  if (a._tag === "user") return guarded ? card() : { _tag: "apply" }
  const lead = leadOf(s)
  if (a._tag === "agent") {
    const level = autonomyOf(s, a)
    if (level === "suggest") forbidden("autonomy", REMEMBER_AT_SUGGEST)
    if (guarded) return card()
    const agent = agentOf(s, a.agentId)
    if (agent && capabilityVerdict(s, agent, "propose_memory") === "propose") return card()
    const run = runOf(s, a.runId)
    const knownTurn = run?.kind === "turn" && run.agentId === a.agentId
    if ((knownTurn && level === null) || level === "act") return { _tag: "apply" }
    if (level === "ask") return card()
    // not one of its turns (a run Minga can't place): the lead may still curate it, as before wave C
    return card(leadMayDecide(a, c.op, c.draft, existing, c.evidence, lead?.id))
  }
  // a worker: its item's lead decides at Act, else a person
  if (guarded) return card()
  const leadActs = lead !== undefined && levelOf(lead) === "act"
  const leadDecides = leadActs && leadMayDecide(a, c.op, c.draft, existing, c.evidence, lead?.id)
  // decision 0158 (10-22 review: 10 of 14 cards were workers' notes, mostly about their test harness): at Act, a
  // worker's new or revised fact or learning that is about no person is saved at once, unchecked, with its run as
  // source; the lead hears of it in its next turn (`## Settled since your last turn`) and tends it like any claim
  // (keep, correct, forget). A forget, a preference and anything about a person stay a card
  if (
    leadDecides &&
    (c.op === "create" || c.op === "revise") &&
    c.draft !== null &&
    (c.draft.content._tag === "fact" || c.draft.content._tag === "learning")
  )
    return { _tag: "apply" }
  return card(leadDecides)
}

/** "Applies at once" (spec §4.3): the memory is written now, by the writer, unchecked unless a person wrote it. */
const applyNow = (
  tx: Tx,
  c: Cmd<"ProposeMemory">,
  existing: MemoryItem | undefined,
  superseded?: MemoryItem,
): void => {
  const a = tx.actor
  const via = a._tag === "user" ? ("person_mcp" as const) : undefined
  const sourceRunId = a._tag === "agent" || a._tag === "worker" ? a.runId : null
  const base = { ownerId: tx.owner(), actor: a, now: tx.now, seq: tx.state.seq }
  // decision 0134 (0133 §3): taint no longer forces a card, so it is recorded here (quoted wherever the memory loads)
  const tainted = actorTaint(tx.state, a) === "tainted"
  const extra = { tainted, reviewedBy: null, proposalId: null, sourceRunId, via }
  switch (c.op) {
    case "create":
      tx.emit({
        _tag: "MemoryItemCreated",
        item: withSupersedes(
          memoryItemOf(base, c.itemId, must(c.draft, "draft"), {
            ...extra,
            revision: 1,
            ref: nextMemoryRef(tx.state),
          }),
          superseded,
        ),
      })
      if (superseded) markSuperseded(tx, superseded, c.itemId)
      return
    case "confirm":
      confirmMemory(tx, c)
      return
    case "revise": {
      const x = must(existing, "memory item")
      tx.emit({
        _tag: "MemoryItemRevised",
        itemId: x.id,
        item: memoryItemOf(base, x.id, must(c.draft, "draft"), {
          ...extra,
          tainted: tainted || x.tainted,
          revision: x.revision + 1,
          createdBy: x.createdBy,
          createdAt: x.createdAt,
          ref: x.ref,
          source: x.source,
        }),
        beforeRevision: x.revision,
      })
      return
    }
    case "archive":
      tx.emit({ _tag: "MemoryItemArchived", itemId: c.itemId, reason: c.reasoning })
      reinstateSuperseded(tx, c.itemId)
      return
    case "promote":
      invalid("promote goes to a person", "op")
  }
}

/**
 * Decision 0146: the current claim a create supersedes (`supersedes`), checked: only with a new claim, in this
 * project, still current (accepted and not superseded already).
 */
const supersededOf = (
  tx: Tx,
  op: Cmd<"ProposeMemory">["op"],
  id: Ids["MemoryItemId"] | undefined,
): MemoryItem | undefined => {
  if (id === undefined) return undefined
  if (op !== "create") invalid("supersedes goes with a new claim (op create)", "supersedes")
  const old = memoryOf(tx.state, id)
  if (!old || !isProjectScope(old.scope))
    invalid("the memory it supersedes is not in this project", "supersedes")
  if (old.supersededBy !== undefined) {
    const by = memoryOf(tx.state, old.supersededBy)
    invalid(
      `${old.ref ?? old.id} is already superseded by ${by?.ref ?? old.supersededBy}: supersede the current claim`,
      "supersedes",
    )
  }
  if (old.status !== "accepted")
    invalid(`${old.ref ?? old.id} is forgotten: write a new memory`, "supersedes")
  return old
}

/** The new claim's back link (and when it starts: its `validFrom`, else now). */
const withSupersedes = (item: MemoryItem, old: MemoryItem | undefined): MemoryItem =>
  old ? { ...item, supersedes: old.id } : item

/**
 * Decision 0146: mark the old claim superseded by `byId`, never deleted: a new revision with `supersededBy` and
 * `validTo` (the newer claim's start, or now; an earlier `validTo` of its own stays).
 */
const markSuperseded = (tx: Tx, old: MemoryItem, byId: Ids["MemoryItemId"]): void => {
  const by = memoryOf(tx.state, byId)
  const start = by?.validFrom ?? tx.now
  const validTo = old.validTo !== undefined && old.validTo < start ? old.validTo : start
  tx.emit({
    _tag: "MemoryItemRevised",
    itemId: old.id,
    item: {
      ...old,
      supersededBy: byId,
      validTo: validTo as MemoryItem["createdAt"],
      revision: old.revision + 1,
      updatedBy: tx.actor,
      updatedAt: tx.now as MemoryItem["createdAt"],
      lastSeq: (tx.state.seq + 1) as Ids["Seq"],
    },
    beforeRevision: old.revision,
  })
}

/**
 * Decision 0146 (`remember{confirm: "M-7"}`): the claim was checked against the record and still holds. Stamps
 * `lastConfirmedAt` in a revision event that keeps the revision number, so a memory delta does not list it as changed.
 */
/**
 * Decision 0146: the undo of a supersession. Forgetting (or revoking) a claim that superseded another brings the older
 * one back into effect: its `supersededBy` and the `validTo` the supersession set are cleared in a new revision.
 */
const reinstateSuperseded = (tx: Tx, forgottenId: Ids["MemoryItemId"]): void => {
  const forgotten = memoryOf(tx.state, forgottenId)
  const oldId = forgotten?.supersedes
  if (oldId === undefined) return
  const old = memoryOf(tx.state, oldId)
  if (!old || old.supersededBy !== forgottenId) return
  const { supersededBy: _by, validTo: _to, ...rest } = old
  tx.emit({
    _tag: "MemoryItemRevised",
    itemId: old.id,
    item: {
      ...rest,
      revision: old.revision + 1,
      updatedBy: tx.actor,
      updatedAt: tx.now as MemoryItem["createdAt"],
      lastSeq: (tx.state.seq + 1) as Ids["Seq"],
    },
    beforeRevision: old.revision,
  })
}

const CONFIRM_REVISIT_MS = 90 * 86_400_000

const confirmMemory = (tx: Tx, c: Cmd<"ProposeMemory">): void => {
  const x = memoryOf(tx.state, c.itemId)
  if (!x || !isProjectScope(x.scope)) invalid("memory item does not exist here", "itemId")
  if (x.status !== "accepted" || x.supersededBy !== undefined)
    invalid(`${x.ref ?? x.id} is not current: confirm the claim in effect`, "itemId")
  tx.emit({
    _tag: "MemoryItemRevised",
    itemId: x.id,
    item: {
      ...x,
      // a confirmed learning is not due again for another 90 days
      ...(x.content._tag === "learning" && x.content.revisitAt <= tx.now
        ? { content: { ...x.content, revisitAt: (tx.now + CONFIRM_REVISIT_MS) as MemoryItem["createdAt"] } }
        : {}),
      lastConfirmedAt: tx.now as MemoryItem["createdAt"],
      lastSeq: (tx.state.seq + 1) as Ids["Seq"],
    },
    beforeRevision: x.revision,
  })
}

/**
 * Apply an approved memory proposal in this log. Decision 0139: a card from before it that names another scope (or a
 * promotion) fails to apply with the reason (`ProposalApplyFailed`, shown on the card), never a silent drop.
 */
export const applyMemoryProposal = (
  tx: Tx,
  p: Proposal,
  body: Extract<ProposalBody, { _tag: "memory" }>,
): void => {
  const existing = memoryOf(tx.state, body.itemId)
  const scope = body.draft?.scope ?? body.fromScope ?? existing?.scope
  const elsewhere = memoryElsewhere(scope, body.op)
  if (elsewhere) invalid(elsewhere, body.op === "promote" ? "op" : "draft.scope")
  if (!scope) invalid("memory item does not exist here", "itemId")
  const base = { ownerId: p.ownerId, actor: tx.actor, now: tx.now, seq: tx.state.seq }
  const extra = { tainted: p.tainted, reviewedBy: tx.actor, proposalId: p.id, sourceRunId: body.sourceRunId }
  switch (body.op) {
    case "create":
    case "promote":
      if (existing && existing.status === "accepted" && body.op === "create")
        invalid("memory item exists", "itemId")
      {
        // decision 0146: the claim it supersedes must still be current when the card is approved
        const old = body.op === "create" ? supersededOf(tx, "create", body.supersedes) : undefined
        tx.emit({
          _tag: "MemoryItemCreated",
          item: withSupersedes(
            memoryItemOf(base, body.itemId, must(body.draft, "draft"), {
              ...extra,
              revision: 1,
              createdBy: p.proposedBy,
              ref: existing?.ref ?? nextMemoryRef(tx.state),
              ...(p.proposedBy._tag === "user" ? { via: "person_mcp" as const } : {}),
            }),
            old,
          ),
        })
        if (old) markSuperseded(tx, old, body.itemId)
      }
      return
    case "revise":
      if (!existing) invalid("memory item does not exist here", "itemId")
      if (existing.locked && tx.actor._tag !== "user")
        forbidden("held", "a locked memory item is changed by a person only")
      tx.emit({
        _tag: "MemoryItemRevised",
        itemId: body.itemId,
        item: memoryItemOf(base, body.itemId, must(body.draft, "draft"), {
          ...extra,
          revision: existing.revision + 1,
          createdBy: existing.createdBy,
          createdAt: existing.createdAt,
          ref: existing.ref,
          source: existing.source,
          via: existing.via,
        }),
        beforeRevision: existing.revision,
      })
      return
    case "archive":
      if (!existing) invalid("memory item does not exist here", "itemId")
      if (existing.locked && tx.actor._tag !== "user")
        forbidden("held", "a locked memory item is changed by a person only")
      tx.emit({ _tag: "MemoryItemArchived", itemId: body.itemId, reason: p.reasoning })
      reinstateSuperseded(tx, body.itemId)
      return
  }
}

/** People write memory directly (project, area and item scopes in this DO). */
export const writeMemory = (tx: Tx, c: Cmd<"WriteMemory">): void => {
  if (tx.actor._tag !== "user") forbidden("actor_kind", "only people write memory directly; agents propose")
  const existing = memoryOf(tx.state, c.itemId)
  refuseNewRule(c.op, c.draft, existing)
  const scope = c.draft?.scope ?? existing?.scope
  if (!scope) invalid("create needs a draft", "draft")
  if (!isProjectScope(scope))
    invalid(`${scope._tag} memory is written in its owner's store, not the project`, "draft.scope")
  if (
    scope._tag !== "project" &&
    tx.state.project &&
    "projectId" in scope &&
    scope.projectId !== tx.state.project.id
  )
    invalid("the scope names another project", "draft.scope")
  const problem = sourceProblem(tx, c.draft?.source, false) ?? aboutProblem(tx.state, c.draft)
  if (problem) invalid(problem, c.draft?.source ? "draft.source" : "draft.content.about")
  const base = { ownerId: tx.owner(), actor: tx.actor, now: tx.now, seq: tx.state.seq }
  const extra = { tainted: false, reviewedBy: null, proposalId: null, sourceRunId: null }
  switch (c.op) {
    case "create":
      if (existing) invalid("memory item id already exists", "itemId")
      if (!c.draft) invalid("create needs a draft", "draft")
      tx.emit({
        _tag: "MemoryItemCreated",
        item: memoryItemOf(base, c.itemId, c.draft, { ...extra, revision: 1, ref: nextMemoryRef(tx.state) }),
      })
      return
    case "revise": {
      if (!existing || !c.draft) invalid("revise needs an existing item and a draft", "itemId")
      // spec §3.4: Edit and Restore keep provenance; taint clears only when the person changes the body, and only that
      // marks the item checked (`reviewedBy`; core memoryChecked). A Restore, or an Edit of other fields, keeps both
      const bodyChanged = c.draft.body !== existing.body || c.draft.title !== existing.title
      tx.emit({
        _tag: "MemoryItemRevised",
        itemId: c.itemId,
        item: memoryItemOf(base, c.itemId, c.draft, {
          tainted: bodyChanged ? false : existing.tainted,
          reviewedBy: bodyChanged ? tx.actor : existing.reviewedBy,
          proposalId: existing.proposalId,
          sourceRunId: existing.sourceRunId,
          revision: existing.revision + 1,
          createdBy: existing.createdBy,
          createdAt: existing.createdAt,
          ref: existing.ref,
          source: existing.source,
          via: existing.via,
        }),
        beforeRevision: existing.revision,
      })
      return
    }
    case "archive":
      if (!existing) invalid("memory item does not exist", "itemId")
      tx.emit({ _tag: "MemoryItemArchived", itemId: c.itemId, reason: c.reason ?? "archived" })
      reinstateSuperseded(tx, c.itemId)
      return
    case "revoke":
      if (!existing) invalid("memory item does not exist", "itemId")
      tx.emit({ _tag: "MemoryItemRevoked", itemId: c.itemId, reason: c.reason ?? "revoked" })
      reinstateSuperseded(tx, c.itemId)
      return
  }
}
